import { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type PanelConfigPatch } from '../common/panel-grid';
import { type PageCmsPanel } from '../common/panel-types';
import { PanelInfoText, PanelLine } from '../CustomPage.styled';
import { ChallengeCarouselPanelEditor } from './panel-editors/ChallengeCarouselPanelEditor';
import { IdeasPanelEditor } from './panel-editors/IdeasPanelEditor';
import { ImagePanelEditor } from './panel-editors/ImagePanelEditor';
import { LeaderboardPanelEditor } from './panel-editors/LeaderboardPanelEditor';
import { TextPanelEditor } from './panel-editors/TextPanelEditor';
import { VideoPanelEditor } from './panel-editors/VideoPanelEditor';

const messages = defineMessages({
  activityInfo: {
    id: 'editCustomPagePanelActivityInfo',
    defaultMessage: 'Displays recent user and challenge activity'
  },
  blogInfo: {
    id: 'editCustomPagePanelBlogInfo',
    defaultMessage: 'Displays latest blog posts'
  }
});

interface PanelContentProps {
  readonly panel: PageCmsPanel;
  readonly showPanelErrors: boolean;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
  readonly idPrefix?: string;
}

const PanelContentComponent = ({ panel, showPanelErrors, onConfigChange, idPrefix = '' }: PanelContentProps) => {
  const intl = useIntl();
  const config = panel.settings;

  switch (config.__typename) {
    case 'LinePanelSettings':
      return <PanelLine data-testid="panel-line" />;
    case 'ActivityPanelSettings':
      return <PanelInfoText data-testid="panel-info-text">{intl.formatMessage(messages.activityInfo)}</PanelInfoText>;
    case 'BlogCarouselPanelSettings':
      return <PanelInfoText data-testid="panel-info-text">{intl.formatMessage(messages.blogInfo)}</PanelInfoText>;
    case 'LeaderboardPanelSettings':
      return <LeaderboardPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'IdeasPanelSettings':
      return <IdeasPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'ChallengeCarouselPanelSettings':
      return <ChallengeCarouselPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'VideoPanelSettings':
      return <VideoPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'TextPanelSettings':
      return (
        <TextPanelEditor
          config={config}
          panelId={panel.id}
          showPanelErrors={showPanelErrors}
          onConfigChange={onConfigChange}
          idPrefix={idPrefix}
        />
      );
    case 'ImagePanelSettings':
      return (
        <ImagePanelEditor
          config={config}
          panelId={panel.id}
          showPanelErrors={showPanelErrors}
          onConfigChange={onConfigChange}
        />
      );
    default:
      return null;
  }
};

export const PanelContent = memo(PanelContentComponent);
